/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { PauseIcon, PlayIcon, SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { notify } from 'app/services/notify.client'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { logError } from 'lib/logging'

interface AudioPlayerProps {
  id: string
  callId?: string
  currentAudioPlaying?: string | number
  waveforms?: string[] | null
  url?: string
  audioDuration?: number
  urlNotFoundMessage?: string
  className?: string
  imageHeight?: string
  onPlay?: (id?: string | number) => void
  seekTo?: number
  onTimeUpdate?: (currentTime: number) => void
}

export default function AudioPlayer({
  id,
  callId,
  currentAudioPlaying,
  waveforms,
  url: audioUrl,
  urlNotFoundMessage,
  className = `py-6`,
  imageHeight = `h-12`,
  onPlay,
  seekTo,
  onTimeUpdate,
}: AudioPlayerProps): React.ReactElement {
  const audioPlayer = useRef<HTMLAudioElement>(null)
  const progressBar = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const waveFormsContainer = useRef<HTMLDivElement>(null)
  let [imageLoaded, setImageLoaded] = useState<boolean>(false)
  let [currentTime, setCurrentTime] = useState<string>(`--:--`)
  let [duration, setDuration] = useState<string>(`--:--`)
  let [isPlaying, setIsPlaying] = useState<boolean>(false)
  let [volume, setVolume] = useState<number>(100)
  let [isMuted, setIsMuted] = useState<boolean>(false)
  let [playbackRate, setPlaybackRate] = useState<number>(1)
  let [audioWaveWidth, setAudioWaveWidth] = useState<number>(0)
  let [showThumb, setShowThumb] = useState<boolean>(false)
  const [audioDuration, setAudioDuration] = useState<number | null>(null)
  const animationFrame = useRef<number | undefined>(undefined)
  useEffect(
    () => () => {
      if (animationFrame.current !== undefined) cancelAnimationFrame(animationFrame.current)
    },
    [],
  )
  const disabledAudioPlayer = useMemo(() => audioDuration == null, [audioDuration])
  const url = useMemo(() => audioUrl, [audioUrl])
  const waveformUrl = useMemo(() => {
    // Render the waveform via the same-origin `/api/calls/{id}/waveform` endpoint. trackable-api
    // generates a crisp, fixed-size PNG (400x30) server-side from the call's S3 audio (TRK-5238),
    // independent of CallRail and the legacy monolith. This produces a uniform waveform for both
    // CallRail calls and AI/11labs calls (which have no CallRail waveform but do have an S3 mp3).
    //
    // We intentionally do NOT use the raw CallRail `waveforms[0]` URL: CallRail serves a retina 2x
    // image, ignores the height param, and returns a blank 404 outside a narrow width range, which
    // trips the <img> onError handler and leaves the wave empty. The `waveforms?.[0]` fallback is
    // only used when no callId is wired.
    //
    // Gate on the recording URL: a call with no recording (callRecordingUrl null) has nothing for
    // the waveform endpoint to render from, so requesting it only produces a guaranteed 404 (TRK-5751).
    if (!audioUrl) return undefined
    if (!callId) return waveforms?.[0]
    return `/api/calls/${callId}/waveform?width=400&height=30`
  }, [audioUrl, callId, waveforms])

  const playHandler = async () => {
    if (url) {
      if (audioPlayer.current!.paused) {
        await audioPlayer.current!.play()
      } else {
        audioPlayer.current!.pause()
        cancelAnimationFrame(animationFrame.current ?? 0)
      }
      onPlay?.(id!)
    } else {
      notify(urlNotFoundMessage ?? `There was an error loading call audio.`)
    }
  }

  const handleMute = () => {
    if (isMuted) {
      audioPlayer.current!.volume = 1
      setVolume(100)
      setIsMuted(false)
    } else {
      audioPlayer.current!.volume = 0
      setVolume(0)
      setIsMuted(true)
    }
  }

  const handleVolume = (event) => {
    audioPlayer.current!.volume = event.target.value / 100
    setVolume(event.target.value)
  }

  const progressAnimation = () => {
    if (!audioPlayer.current) return
    let current = audioPlayer?.current?.currentTime ?? 0
    let duration = audioPlayer.current.duration
    let percentage = current / duration
    if (progressBar.current) progressBar.current.style.width = `${percentage * audioWaveWidth}px`
  }

  const handlePlayBackRate = () => {
    let speed = playbackRate
    if (speed === 3) {
      speed = 0.5
      setPlaybackRate(0.5)
    } else {
      speed = speed + 0.5
      setPlaybackRate(speed)
    }
    audioPlayer.current!.playbackRate = speed
  }

  const handleCurrentTime = () => {
    if (!audioPlayer.current) return
    if (audioPlayer.current.ended) {
      setIsPlaying(false)
      cancelAnimationFrame(animationFrame.current ?? 0)
    }
    onTimeUpdate?.(audioPlayer.current.currentTime)
    setCurrentTime(moment.utc(audioPlayer.current.currentTime * 1000).format(`mm:ss`))
    animationFrame.current = requestAnimationFrame(progressAnimation)
  }

  const handleSeak = (event) => {
    if (!audioPlayer.current || !progressBar.current?.parentElement) return
    let barContainer = progressBar.current.parentElement!
    let value = (barContainer.getBoundingClientRect().right - event.clientX) / audioWaveWidth
    value = 1 - value
    let duration = audioPlayer.current.duration
    if (isNaN(duration)) return
    audioPlayer.current.currentTime = duration * value
    progressBar.current.style.width = `${Math.round((audioPlayer.current.currentTime / duration) * 100)}%`
  }

  useEffect(() => {
    const container = progressBar.current?.parentElement
    if (!container) return
    const observer = new ResizeObserver((entries) => {
      setAudioWaveWidth(entries[0].contentRect.width ?? 0)
      const player = audioPlayer.current
      const bar = progressBar.current
      if (player && bar && player.duration > 0) {
        bar.style.width = `${Math.round((player.currentTime / player.duration) * 100)}%`
      }
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [progressBar?.current])

  useEffect(() => {
    if (currentAudioPlaying !== id && isPlaying) {
      audioPlayer.current!.pause()
      cancelAnimationFrame(animationFrame.current ?? 0)
      setIsPlaying(false)
    }
  }, [currentAudioPlaying])

  useEffect(() => {
    if (audioDuration && audioDuration > 0) {
      let minutes = Math.floor(audioDuration / 60)
      let seconds = audioDuration % 60
      setDuration(`${minutes.toString().padStart(2, `0`)}:${seconds.toString().padStart(2, `0`)}`)
      setCurrentTime(`00:00`)
    }
  }, [audioDuration])

  useEffect(() => {
    setAudioWaveWidth(waveFormsContainer?.current?.offsetWidth ?? 0)
  }, [waveFormsContainer, waveFormsContainer?.current?.offsetWidth])

  useEffect(() => {
    if (seekTo && audioPlayer.current && progressBar.current) {
      audioPlayer.current.currentTime = seekTo
      progressBar.current.style.width = `${Math.round(
        (audioPlayer.current.currentTime / audioPlayer.current.duration) * 100,
      )}%`
      if (audioPlayer.current.paused || audioPlayer.current.ended) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        audioPlayer.current.play()
      }
    }
  }, [seekTo])
  return (
    <>
      <audio
        ref={audioPlayer}
        src={url}
        preload="metadata"
        autoPlay={false}
        onLoadedMetadata={(event) => {
          if (!audioDuration && event.currentTarget.duration) {
            setAudioDuration(Math.floor(event.currentTarget.duration))
          }
        }}
        onTimeUpdate={handleCurrentTime}
        onPlay={() => {
          setIsPlaying(true)
        }}
        onPause={() => {
          setIsPlaying(false)
        }}
        onError={(err) => {
          logError(`error playing audio`, url, `error:`, err)
          //TODO: TRK-3509 - handle more specific errors
          // notifyError(`There was an error playing the audio`)
        }}
      />
      <div className={classNames(`flex w-full items-center gap-x-4 align-middle`, className)}>
        <div className="flex justify-between gap-2 align-middle">
          <div className="group flex justify-center">
            <div className="relative">
              <div
                className={classNames(
                  `invisible absolute -top-1 left-1/2 flex h-10 -translate-x-1/2 -translate-y-full justify-center rounded bg-transparent`,
                  url && !disabledAudioPlayer ? `group-hover:visible` : ``,
                  { 'bg-(--fg-minimal)': disabledAudioPlayer },
                )}
              >
                <Input
                  inline
                  size="sm"
                  id="audioPlayerVolume"
                  type="range"
                  value={volume}
                  min="0"
                  max="100"
                  onChange={handleVolume}
                  disabled={disabledAudioPlayer}
                  className="relative w-20 cursor-pointer"
                />
              </div>
              <div>
                {!isMuted && (
                  <SpeakerWaveIcon
                    className={classNames(
                      `h-7.5 w-7.5 rounded-full border border-default p-2`,
                      url ? `cursor-pointer` : ``,
                      disabledAudioPlayer ? `pointer-events-none bg-surface-strong` : ``,
                    )}
                    onClick={() => {
                      if (url && !disabledAudioPlayer) handleMute()
                    }}
                  />
                )}
                {isMuted && (
                  <SpeakerXMarkIcon
                    className="h-7.5 w-7.5 cursor-pointer rounded-full border border-default p-2"
                    onClick={handleMute}
                  />
                )}
              </div>
            </div>
          </div>
          <div id="play-button">
            {!isPlaying && (
              <PlayIcon
                className={classNames(
                  `h-7.5 w-7.5 rounded-full border p-2`,
                  disabledAudioPlayer ? `pointer-events-none bg-surface-strong` : `cursor-pointer bg-blue-600 `,
                )}
                color="#FFFFFF"
                onClick={async () => {
                  if (!disabledAudioPlayer) await playHandler()
                }}
              />
            )}
            {isPlaying && (
              <PauseIcon
                className={classNames(
                  `h-7.5 w-7.5 rounded-full border  p-2`,
                  disabledAudioPlayer ? `pointer-events-none bg-surface-strong` : `cursor-pointer  bg-blue-600`,
                )}
                color="#FFFFFF"
                onClick={playHandler}
              />
            )}
          </div>
          <Button
            variant="white"
            size="icon-sm"
            shape="pill"
            type="button"
            className="h-7.5 w-7.5 cursor-default"
            disabled={!url || disabledAudioPlayer}
            onClick={() => {
              if (url) {
                handlePlayBackRate()
              }
            }}
          >
            {playbackRate}x
          </Button>
        </div>
        <div
          className={`relative flex w-full ${
            showThumb ? `grow` : `grow-0`
          } cursor-pointer items-center hover:opacity-100`}
          onClick={handleSeak}
        >
          <div
            ref={waveFormsContainer}
            className={`absolute ${showThumb ? `` : `top-1/2`} w-full flex-auto rounded bg-transparent`}
          >
            {showThumb ? (
              <div className="flex w-full cursor-pointer rounded-full bg-surface-strong">
                <div ref={progressBar} className="h-2 flex-none rounded-l-full bg-blue-600"></div>
                <div className="-my-1.25 -mr-0.5 h-4.5 w-1.5 shrink-0 rounded-full bg-blue-600"></div>
              </div>
            ) : (
              <div
                ref={progressBar}
                className={`h-0.5 flex-none rounded-full ${
                  audioDuration && progressBar?.current?.style?.width ? `bg-blue-600` : ``
                }`}
              />
            )}
          </div>
          {waveformUrl ? (
            <img
              alt="audio waves"
              src={waveformUrl}
              ref={imgRef}
              className={classNames(`w-full`, !imageLoaded && `hidden`, imageHeight)}
              onError={() => setShowThumb(true)}
              onLoad={() => {
                setImageLoaded(true)
              }}
            />
          ) : null}
        </div>
        <div className="flex w-20 shrink-0 items-center justify-evenly text-ui-xs text-default">
          <span className="shrink-0">{currentTime}</span>
          <span>/</span>
          <span>{duration}</span>
        </div>
      </div>
    </>
  )
}
